import { DOWNLOAD_FILE_URL, UPLOAD_FILE_URL } from "utils/constants/config"
import { get, post, put, remove } from "./api"

/**
 * Get all job roles
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getAllJobRoles = (token, params) => {
  return get({
    url: '/job_role/list',
    token: token,
    params: params,
  })
}

/**
 * Create job role
 * @param {string} token - user authentication token
 * @param {object} data - request data
 */
export const createJobRole = (token, data) => {
  return post({
    url: '/job_role/add_record',
    token: token,
    data: data,
  })
}

/**
 * Update job role
 * @param {string} token - user authentication token
 * @param {string} jobRoleUUID - job role uuid
 * @param {object} data - request data
 */
export const updateJobRole = (token, jobRoleUUID, data) => {
  return put({
    url: `/job_role/${jobRoleUUID}`,
    token: token,
    data: data,
  })
}

/**
 * Create occupation area
 * @param {string} token - user authentication token
 * @param {object} data - request data
 */
export const createOccupationArea = (token, data) => {
  return post({
    url: '/occupation_area/add_record',
    token: token,
    data: data,
  })
}

/**
 * Get occupation areas
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getOccupationAreas = (token, params) => {
  return get({
    url: '/occupation_area/list',
    token: token,
    params: params,
  })
}

/**
 * update occupation area
 * @param {string} token - user authentication token
 * @param {string} occupationAreaUUID - occupation area uuid
 * @param {object} data - request data
 */
export const updateOccupationArea = (token, occupationAreaUUID, data) => {
  return put({
    url: `/occupation_area/${occupationAreaUUID}`,
    token: token,
    data: data,
  })
}

/**
 * Get all CNAE records
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getAllCnaesList = (token, params) => {
  return get({
    url: '/cnaes/list',
    token: token,
    params: params,
  })
}

/**
 * Count all CNAE records
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getCountAllCnaes = (token, params) => {
  return get({
    url: '/cnaes/count',
    token: token,
    params: params,
  })
}


/**
 * Get all employee records
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getAllEmployeesList = (token, params) => {
  return get({
    url: '/employees/list',
    token: token,
    params: params,
  })
}

/**
 * Get the number of emplyee records
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getCountAllEmployees = (token, params) => {
  return get({
    url: '/employees/count',
    token: token,
    params: params,
  })
}

/**
 * Get all compliance lists
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getAllComplianceList = (token, params) => {
  return get({
    url: '/compliance_list/list',
    token: token,
    params: params,
  })
}

/**
 * Get the number of records in the compliance list
 * @param {string} token - user authentication token
 * @param {object} params - request parameters
 */
export const getCountAllComplianceList = (token, params) => {
  return get({
    url: '/compliance_list/count',
    token: token,
    params: params,
  })
}

/**
 * Update compliance list record
 * @param {string} token - user authentication token
 * @param {object} data - request data
 */
export const updateComplianceListRecord = (token, data) => {
  return put({
    url: `/compliance_list`,
    token: token,
    data: data,
  })
}

/**
 * Create compliance list record
 * @param {string} token - user authentication token
 * @param {object} data - request data
 */
export const createComplianceListRecord = (token, data) => {
  return post({
    url: `/compliance_list`,
    token: token,
    data: data,
  })
}

/**
 * Delete compliance list record
 * @param {string} token - user authentication token
 * @param {string} recordUUID - record uuid
 */
export const deleteComplianceListRecord = (token, recordUUID) => {
  return remove({
    url: `/compliance_list/${recordUUID}`,
    token: token,
  })
}

/**
 * Upload a configuration file. The url must be mappend on UPLOAD_FILE_URL constants.
 * @param {string} token - user authentication token
 * @param {object} file - the file taken from <input>
 * @param {string} fileType - must be mapped on UPLOAD_FILE_TYPE with a matching UPLOAD_FILE_URL
 */
export const postUploadConfigFile = (token, file, fileType) => {
  const formData = new FormData()
  formData.append('file', file)

  return post({
    url: `/${UPLOAD_FILE_URL[fileType]}`,
    token: token,
    data: formData,
    contentType: file.type,
    throwErrors: true,
  })
}


/**
 * Get config file as bytes. The url must be mappend on DOWLOAD_FILE_URL constants.
 * @param {string} token - user authentication token
 * @param {object} file - the file taken from <input>
 * @param {string} fileType - must be mapped on DOWLOAD_FILE_TYPE with a matching DOWLOAD_FILE_URL
 */
export const getDownloadConfigFile = (token, fileType) => {
  const res = get({
    url: `/${DOWNLOAD_FILE_URL[fileType]}`,
    token: token,
    responseType: 'blob',
    throwErrors: true,
  })
  return res
}



/**
 * Get the most recent upload file logs.
 * @param {string} token - user authentication token
 * @param {string} fileType - must be mapped on UPLOAD_FILE_TYPE
 */
export const getUploadFileLogs = (token, fileType) => {
  return get({
    url: `/uploads/config/logs`,
    params: { file_type:  fileType },
    token: token,
    throwErrors: true,
  })
}

/**
 * List users.
 * @param {string} token - user authentication token
 * @param {object} data - filters
 */
export const listUsers = (token, data = { name_or_email: null, user_role: null, page: 1 }) => {
  return get({
    url: `/user/list/paginated`,
    params: { name_or_email: data.name, user_role: data.user_role, page: data.page, size: 10 },
    token: token,
    throwErrors: true,
  })
}

/**
 * Create user
 * @param {string} token - user authentication token
 * @param {object} data - an object with the creation data
 */
export const postCreateUser = (token, data) => {

  return post({
    url: `/user/create`,
    token: token,
    data: data,
    throwErrors: true,
  })
}


/**
 * Create user
 * @param {string} token - user authentication token
 * @param {object} data - an object with the creation data
 */
export const putEditUser = (token, data) => {

  return put({
    url: `/user/edit`,
    token: token,
    data: data,
    throwErrors: true,
  })
}
